/**
 * "Is this transcript line the learner's?" — asked positively, in one place.
 *
 * Every consumer used to answer by negation: a line belonged to the rep unless it
 * started with the literal `Customer: ` prefix `conversationFromChat` writes. That
 * only holds while the buyer is the ONLY other speaker. As soon as a second persona
 * speaks under its own name, negation hands its lines to the learner, and they get
 * scored, word-counted, highlighted and annotated as rep speech.
 *
 * Which label is a speaker, and what a line says, is decided by the transcript
 * reader (`speaker-names.js`, `speaker-line.js`). This module only answers which
 * side of the conversation a speaker is on.
 */

import {
  MAX_LABEL_WORDS,
  NON_REP_SPEAKER_LABELS,
  REP_SPEAKER_LABELS,
  sameSpeakerName,
  speakerKey,
} from "@/lib/transcript/speaker-names";
import { splitSpeakerLine } from "@/lib/transcript/speaker-line";

const MAX_LABEL_LENGTH = 40;

/**
 * The speaker of a raw line or of an already-parsed message.
 *
 * @param {string|{speaker?: string}|null} entry
 * @param {Object} [options] - Same options as `isRepSpeaker`. Its names head a
 *   raw line whatever their shape ("sam: hello").
 * @returns {string|null}
 */
export function speakerOf(entry, options = {}) {
  if (!entry) return null;
  if (typeof entry === "string") {
    const { learnerName, learnerNames = [], personaNames = [] } = options;
    const knownSpeakers = [learnerName, ...learnerNames, ...personaNames];
    return splitSpeakerLine(entry, { knownSpeakers })?.speaker ?? null;
  }
  if (typeof entry.speaker === "string") return entry.speaker;
  return null;
}

/**
 * Is this speaker the learner?
 *
 * @param {unknown} speaker
 * @param {Object} [options]
 * @param {string} [options.learnerName] - Whatever the transcript writer used as
 *   the learner's prefix (usually the session user's name).
 * @param {Array<string>} [options.personaNames] - Everyone in the cast. Supplying
 *   this is what stops a second persona being scored as rep speech.
 * @param {Array<string>} [options.learnerNames] - Every other label the learner
 *   speaks under. An imported meeting is labelled by role ("Manager / Meeting
 *   Facilitator"), not by the learner's name, so the caller that knows which
 *   role is the learner's passes it here.
 * @param {boolean} [options.unknownIsRep] - Verdict for a label we cannot place.
 *   Defaults to true, which is exactly what "anything that is not Customer: is the
 *   rep" meant before personas had names.
 * @returns {boolean}
 */
export function isRepSpeaker(speaker, options = {}) {
  const {
    learnerName = "",
    personaNames = [],
    learnerNames = [],
    unknownIsRep = true,
  } = options;

  const key = speakerKey(speaker);
  if (!key) return unknownIsRep;

  const learnerLabels = [learnerName, ...learnerNames].filter(
    (name) => typeof name === "string" && name.trim(),
  );

  // Exact matches first, on BOTH sides. `sameSpeakerName` counts a whole-word
  // prefix as the same person, so with persona "Sam" and learner "Sam Lee" a
  // persona-first prefix test would classify every "Sam Lee:" turn as the buyer
  // and drop the learner's own speech out of scoring and annotation.
  //
  // The learner wins an exact tie. A persona named after the learner (a studio
  // admin practising against a copy of themselves) is written as "Customer:"
  // in a single-persona session, so a line under the learner's own name can
  // only be the learner's; persona-first here put every one of their turns on
  // the buyer's side of the transcript.
  if (learnerLabels.some((name) => speakerKey(name) === key)) return true;
  if (personaNames.some((name) => speakerKey(name) === key)) return false;

  // Only ambiguous prefix matches left. One side matching is an answer; both
  // sides matching is not, so that case falls through to the label tables rather
  // than forcing the persona verdict.
  const prefixPersona = personaNames.some((name) => sameSpeakerName(key, name));
  const prefixLearner = learnerLabels.some((name) => sameSpeakerName(key, name));
  if (prefixPersona && !prefixLearner) return false;
  if (prefixLearner && !prefixPersona) return true;

  if (NON_REP_SPEAKER_LABELS.has(key)) return false;
  if (REP_SPEAKER_LABELS.has(key)) return true;

  return unknownIsRep;
}

/**
 * Is this line the learner's? Accepts a raw `"Name: text"` line or a parsed
 * `{ speaker, message|content }` object. An unlabelled continuation line inherits
 * the caller's default, which keeps line-filtering consumers behaving as before.
 *
 * @param {string|{speaker?: string}|null} entry
 * @param {Object} [options] - Same options as `isRepSpeaker`.
 * @returns {boolean}
 */
export function isRepLine(entry, options = {}) {
  const speaker = speakerOf(entry, options);
  if (speaker === null) return options.unknownIsRep ?? true;
  return isRepSpeaker(speaker, options);
}

/**
 * Who spoke in this transcript, and did more than one persona take part?
 *
 * The UI needs this to decide between today's fixed "Customer"/"You" labels and
 * rendering real speaker names: a session is multi-speaker only when two distinct
 * non-rep speakers appear, so every single-persona transcript keeps its labels.
 *
 * @param {Array<string|{speaker?: string}>} messages
 * @param {Object} [options] - Same options as `isRepSpeaker`.
 * @returns {{ speakers: Array<{speaker: string, isRep: boolean}>, personaSpeakers: Array<string>, isMultiSpeaker: boolean }}
 */
export function classifyTranscriptSpeakers(messages, options = {}) {
  const speakers = [];
  const seen = new Set();

  for (const message of Array.isArray(messages) ? messages : []) {
    const speaker = speakerOf(message, options);
    const key = speakerKey(speaker);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    speakers.push({ speaker, isRep: isRepSpeaker(speaker, options) });
  }

  const personaSpeakers = speakers
    .filter((entry) => !entry.isRep)
    .map((entry) => entry.speaker);

  return { speakers, personaSpeakers, isMultiSpeaker: personaSpeakers.length > 1 };
}

/**
 * Normalize a persona's name into a label that is safe to write as a transcript
 * prefix: a colon inside a name would open a false speaker boundary, and a
 * newline would split the turn in half. Length and word count are capped so every
 * parser downstream still recognises the label as a name.
 *
 * This is the ONLY place a persona's name is rewritten, and only for the prefix —
 * nothing else renames a persona.
 *
 * @param {unknown} name
 * @returns {string} Empty when there is nothing usable, so callers can fall back.
 */
export function transcriptSpeakerLabel(name) {
  if (typeof name !== "string") return "";

  const cleaned = name
    .replace(/[:\r\n\t]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!cleaned) return "";

  const label = cleaned.split(" ").slice(0, MAX_LABEL_WORDS).join(" ");
  if (label.length <= MAX_LABEL_LENGTH) return label;

  const clipped = label.slice(0, MAX_LABEL_LENGTH);
  return clipped.replace(/\s+\S*$/, "").trim() || clipped.trim();
}
